import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useState } from 'react';
import AnimatedContent from './parts/animated-content.js';
import { cn, tw } from '../../utils/classnames.js';
import LoadingSpinner from '../loading-spinner/index.js';
const baseStyle = tw `ui-relative ui-inline-flex ui-shrink-0 ui-items-center ui-justify-center ui-font-medium ui-transition-opacity`;
const roundedBaseStyle = tw `ui-rounded-full ui-border ui-border-solid ui-border-black/6 ui-text-[13px] ui-uppercase ui-text-black ui-transition-opacity hover:ui-opacity-70 theme-dark:ui-border-white/[0.0355] theme-dark:ui-bg-transparent theme-dark:ui-text-white`;
const variantStyles = {
    primary: tw `ui-bg-black ui-text-white theme-dark:ui-bg-white theme-dark:ui-text-black`,
    secondary: tw `ui-border ui-border-solid ui-border-black ui-bg-transparent ui-text-black theme-dark:ui-border-white theme-dark:ui-text-white`,
    tertiary: tw `ui-h-10 ui-bg-transparent ui-px-2 ui-text-black theme-dark:ui-text-white`,
    rounded: tw `${roundedBaseStyle} ui-bg-white`,
    'rounded-shaded': tw `${roundedBaseStyle} ui-bg-gray-100`,
};
const activeVariantStyles = {
    'rounded-shaded': tw `!ui-bg-black !ui-text-white theme-dark:!ui-bg-white theme-dark:!ui-text-black`,
};
function getRoundedVariantPaddingStyle({ size, hasPrefix, hasSuffix }) {
    let pl;
    let pr;
    if (size === 'large') {
        pl = tw `ui-pl-8`;
        pr = tw `ui-pr-8`;
        if (hasPrefix) {
            pl = tw `ui-pl-4`;
        }
        if (hasSuffix) {
            pr = tw `ui-pr-4`;
        }
    }
    if (size === 'medium') {
        pl = tw `ui-pl-[18px]`;
        pr = tw `ui-pr-[18px]`;
        if (hasPrefix) {
            pl = tw `ui-pl-[7px]`;
        }
        if (hasSuffix) {
            pr = tw `ui-pr-[7px]`;
        }
    }
    if (size === 'small') {
        pl = tw `ui-pl-2`;
        pr = tw `ui-pr-2`;
        if (hasPrefix) {
            // TODO: Adjust.
            pl = tw `ui-pl-2`;
        }
        if (hasSuffix) {
            // TODO: Adjust.
            pr = tw `ui-pr-2`;
        }
    }
    return cn(pl, pr);
}
function Button({ children, className, disabled, loading, active, onClick, prefix, suffix, variant = 'primary', size = 'medium', type = 'button', noShadow = false, animateOnHover = false, changeBgOnHover = false, slideBgOnHover = false, dataTestId, }) {
    const hasPadding = ['primary', 'secondary'].includes(variant);
    const hasShadow = ['primary', 'rounded'].includes(variant) && !noShadow;
    const hasPrefix = !!prefix;
    const hasSuffix = !!suffix;
    const shouldChildrenGrow = variant.startsWith('rounded') && (hasPrefix || hasSuffix);
    const [isHovered, setIsHovered] = useState(false);
    const [animationTriggered, setAnimationTriggered] = useState(false);
    const handleMouseEnter = () => {
        setIsHovered(true);
        setAnimationTriggered(true);
    };
    const handleMouseLeave = () => {
        setIsHovered(false);
        setAnimationTriggered(true);
    };
    return (_jsxs("button", { className: cn(baseStyle, variantStyles[variant], !variant.startsWith('rounded') && size === 'large' && 'ui-rounded-2xl', !variant.startsWith('rounded') &&
            size === 'medium' &&
            'ui-rounded-[9px]', // 1/2 of `Box`'s border radius.
        !variant.startsWith('rounded') && size === 'small' && 'ui-rounded-md', variant !== 'tertiary' && size === 'large' && 'ui-py-[14px]', variant !== 'tertiary' && size === 'medium' && 'ui-py-[7px]', variant !== 'tertiary' &&
            size === 'small' &&
            'ui-leading-1 ui-py-1 ui-text-[11px]', variant.startsWith('rounded') &&
            getRoundedVariantPaddingStyle({ size, hasPrefix, hasSuffix }), hasPadding && size === 'large' && 'ui-px-8', hasPadding && size === 'medium' && 'ui-px-6', hasPadding && size === 'small' && 'ui-px-2', hasShadow && size === 'large' && 'ui-shadow-lg', hasShadow && size === 'medium' && 'ui-shadow-md', !loading && 'disabled:ui-opacity-30', changeBgOnHover &&
            'ui-transition-colors ui-duration-[450ms] ui-ease-in-out hover:ui-bg-gray-200 theme-dark:hover:ui-bg-neutral-600', slideBgOnHover &&
            'ui-relative ui-bg-gradient-to-l ui-from-white ui-from-50% ui-to-black ui-to-50% ui-bg-[length:200%_100%] ui-bg-right ui-transition-all ui-duration-[500ms] ui-ease-out hover:ui-bg-left hover:ui-text-white', active && activeVariantStyles[variant], className), disabled: disabled || loading, onClick: onClick, onMouseEnter: handleMouseEnter, onMouseLeave: handleMouseLeave, type: type, "data-testid": dataTestId, children: [loading && (_jsx("span", { className: "ui-absolute ui-left-1/2 ui-top-1/2 ui-h-1/2 -ui-translate-x-1/2 -ui-translate-y-1/2", children: _jsx(LoadingSpinner, { className: "ui-size-full" }) })), _jsxs(AnimatedContent, { isHovered: isHovered, animationTriggered: animationTriggered, animateOnHover: animateOnHover, className: cn('ui-flex ui-flex-row ui-items-center ui-justify-center ui-gap-x-2', size === 'small' && 'ui-gap-x-1', shouldChildrenGrow && 'ui-w-full ui-gap-x-0.5', loading && 'ui-invisible'), children: [prefix, _jsx("span", { className: cn(shouldChildrenGrow && 'ui-flex-1', 'ui-line-clamp-2 ui-overflow-hidden ui-text-ellipsis'), children: children }), suffix] })] }));
}
export default Button;
